import React, {useEffect, useState} from 'react';
import {useConfirmation} from './ConfirmationContext';
import {useAlert} from './AlertContext';
import {useAgentApi} from '../Services/AgentApiProvider';
import Table from './Tables';
import Button from './Buttons';
import {formatDateUTC} from '../utils/formatDateUTC';
import AgentInstructionEdit from "./AgentInstructionEdit";
import "./SearchInput.css";
import SearchInput from "./SearchInput";

function AgentInstructionsList({
                                   agentId,
                                   canEdit,
                                   agentInstructions = [],
                                   onUpdateTrigger,
                                   onSearch,
                                   onUpdateList,
                                   loading,
                                   totalInstructions,
                                   itemsPerPage,
                                   onChangePage,
                               }) {
    const {showConfirmation} = useConfirmation();
    const {showAlert} = useAlert();
    const {deleteAgentInstruction} = useAgentApi();

    const [editingInstruction, setEditingInstruction] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 500);

        return () => {
            clearTimeout(handler);
        };
    }, [searchTerm]);

    useEffect(() => {
        if (onSearch) {
            onSearch(debouncedSearchTerm);
        }
    }, [debouncedSearchTerm, onSearch]);

    const columns = [
        {
            name: 'Text',
            selector: (row) => {
                const original_text = row.original_text ?? '';
                const limit = 60;

                return original_text.length > limit
                    ? `${original_text.substring(0, limit)}...`
                    : original_text;
            },
            sortable: false
        },
        {
            name: 'Instruction',
            selector: (row) => {
                const instructionText = row.instruction ?? '';
                const limit = 60;

                return instructionText.length > limit
                    ? `${instructionText.substring(0, limit)}...`
                    : instructionText;
            },
            sortable: false,
        },
        {name: 'Status', selector: (row) => (row.is_active ? "Active" : "InActive"), sortable: false},
        {name: 'Updated', selector: (row) => formatDateUTC(row.updated_at), sortable: false},
        {name: 'Created', selector: (row) => formatDateUTC(row.created_at), sortable: false},
    ];

    const handleEditClick = (row) => {
        setEditingInstruction(row);
    };

    const handleCreateClick = () => {
        setEditingInstruction({
            original_text: '',
            instruction: '',
            is_active: true,
        });
    };


    const handleDelete = (selectedId) => {
        if (!selectedId || !canEdit) return;
        showConfirmation('Are you sure you want to delete this instruction?', async () => {
            try {
                await deleteAgentInstruction(agentId, selectedId);
                if (onUpdateList) onUpdateList();
                onUpdateTrigger();
            } catch (error) {
                showAlert('fail', 'Failed to delete the instruction.');
            }
        });
    };

    return (
        <div className="radius-yes p-5 bg-yes col-12">
            {editingInstruction ? (
                <AgentInstructionEdit
                    agentId={agentId}
                    initialData={editingInstruction}
                    canEdit={canEdit}
                    onClose={() => setEditingInstruction(null)}
                    onUpdate={() => {
                        setEditingInstruction(null);
                        if (onUpdateList) onUpdateList();
                    }}
                    onUpdateTrigger={onUpdateTrigger}
                />
            ) : (
                <>
                    <h2 className="mb-4">
                        <SearchInput
                            searchTerm={searchTerm}
                            onSearchChange={setSearchTerm}
                            placeholder="Search instructions..."
                            />
                    </h2>
                    <Table
                        title="Instructions"
                        columns={columns}
                        data={agentInstructions}
                        onRowClicked={handleEditClick}
                        onDelete={handleDelete}
                        selectableRows={canEdit}
                        selectableRowsHighlight
                        pagination
                        paginationServer
                        progressPending={loading}
                        paginationTotalRows={totalInstructions}
                        paginationPerPage={itemsPerPage}
                        onChangePage={onChangePage}
                    />
                    {canEdit && (
                        <div className="d-flex justify-start" style={{marginTop: '1rem'}}>
                            <Button
                                size="large"
                                onClick={handleCreateClick}
                            >
                                Create New Instruction
                            </Button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

export default AgentInstructionsList;